import { CheckCircle, AlertTriangle, Circle } from 'lucide-react';
import { CourseAssignment } from '../types';

interface CourseCardProps {
    course: CourseAssignment;
    onViewCourse: (courseId: string) => void;
}

const calculateProgress = (status: string): number => {
    // Since we don't have lessons, we'll use the status to determine progress
    switch (status) {
        case 'completed':
            return 100;
        case 'inProgress':
            return 50; // Assume 50% for in-progress courses
        case 'due':
            return 25; // Assume 25% for due courses
        case 'notStarted':
        default:
            return 0;
    }
};

export function CourseCard({ course, onViewCourse }: CourseCardProps) {
    const progress = calculateProgress(course.status);
    const isCompleted = course.status === 'completed';
    const isInProgress = course.status === 'inProgress';
    const isDue = course.status === 'due';
    let statusElement;
    if (isCompleted) {
        statusElement = (
            <div className="flex items-center text-green-600 text-xs mb-3">
                <CheckCircle size={14} className="mr-1"/>
                Completed: {course.completed_date ? new Date(course.completed_date).toLocaleDateString() : ''}
            </div>
        );
    } else if (isInProgress) {
        statusElement = (
            <>
                <div className="w-full bg-slate-200 rounded-full h-2 mb-1">
                    <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="flex items-center justify-between text-xs mb-3">
                    <span className="text-slate-500">In Progress: {progress}%</span>
                    {course.due_date && (
                        <span className="text-slate-700 font-bold">Due: {new Date(course.due_date + 'T00:00:00').toLocaleDateString()}</span>
                    )}
                </div>
            </>
        );
    } else if (isDue) {
        statusElement = (
            <div className="flex items-center text-orange-600 text-xs mb-3">
                <AlertTriangle size={14} className="mr-1"/>
                Due: {new Date(course.due_date + 'T00:00:00').toLocaleDateString()}
            </div>
        );
    } else {
        statusElement = (
            <div className="flex items-center justify-between text-xs mb-3">
                <div className="flex items-center text-slate-500">
                    <Circle size={14} className="mr-1"/>
                    Not Started
                </div>
                {course.due_date && (
                    <span className="text-slate-700 font-bold">Due: {new Date(course.due_date + 'T00:00:00').toLocaleDateString()}</span>
                )}
            </div>
        );
    }

    let buttonText = "Start Course";
    let buttonClass = "w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700";
    
    if (isCompleted) {
        buttonText = "Review Course";
        buttonClass = "w-full px-4 py-2 text-sm font-medium text-foreground bg-background rounded-lg border border-border hover:bg-secondary";
    } else if (isInProgress) {
        buttonText = "Continue Course";
        buttonClass = "w-full px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700";
    }

    return (
        <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-slate-200 flex flex-col">
            <img 
                src={course.courses.thumbnail || `https://placehold.co/600x400/E0F2FE/0EA5E9?text=${encodeURIComponent(course.courses.title)}`} 
                alt={`${course.courses.title} thumbnail`} 
                className="w-full h-40 object-cover"
            />
            <div className="p-4 flex flex-col flex-grow">
                <h4 className="font-semibold text-slate-800 mb-1">{course.courses.title}</h4>
                <p className="text-sm text-slate-500 mb-3 line-clamp-2 flex-grow">{course.courses.description}</p>
                <div className="mt-auto">
                    {statusElement}
                    <button onClick={() => onViewCourse(course.course_id)} className={buttonClass}>
                        {buttonText}
                    </button>
                </div>
            </div>
        </div>
    );
}